"use client";

import { c as _c } from "react/compiler-runtime";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { publicEnv } from "@/config/env";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { AlchemyContractMetadataResponse, AlchemyGetNftsForOwnerResponse, AlchemySearchResponse, AlchemyTokenMetadataResponse, OwnerNft, SearchContractsResult } from "@/services/alchemy/types";
import { normaliseAddress, processContractMetadataResponse, processOwnerNftsResponse, processSearchResponse, processTokenMetadataResponse } from "@/services/alchemy/utils";
import type { ContractOverview, Suggestion, SupportedChain, TokenMetadata } from "@/types/nft";
const SUGGESTION_TTL = 60_000;
const CONTRACT_TTL = 5 * 60_000;
const TOKEN_TTL = 60_000;
type CacheEntry<T> = {
  data: T;
  expires: number;
};
const suggestionCache = new Map<string, CacheEntry<SearchContractsResult>>();
const contractCache = new Map<string, CacheEntry<ContractOverview | null>>();
const tokenCache = new Map<string, CacheEntry<TokenMetadata[]>>();
function getBackendAlchemyProxyUrl(path: string): string {
  return `${publicEnv.API_ENDPOINT}/alchemy-proxy${path}`;
}
function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === "AbortError") {
    return true;
  }
  if (error instanceof Error && error.name === "AbortError") {
    return true;
  }
  return false;
}
async function fetchJson<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const response = await fetch(input, init);
  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }
  return (await response.json()) as T;
}
async function fetchJsonWithFailover<T>(primaryUrl: string, backendPath: string, init?: RequestInit): Promise<T> {
  try {
    return await fetchJson<T>(primaryUrl, init);
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    const backendUrl = getBackendAlchemyProxyUrl(backendPath);
    console.warn(`Failed to fetch from primary endpoint (${primaryUrl}), falling back to proxy endpoint: (${backendUrl})`);
    return fetchJson<T>(backendUrl, init);
  }
}
type TokenMetadataParams = {
  readonly address?: `0x${string}`;
  readonly tokenIds?: readonly string[];
  readonly tokens?: readonly {
    contract: string;
    tokenId: string;
  }[];
  readonly chain?: SupportedChain;
  readonly signal?: AbortSignal;
};
async function fetchCollectionsFromApi(params: UseCollectionSearchParams & {
  readonly signal?: AbortSignal;
}): Promise<SearchContractsResult> {
  const {
    query,
    chain = "ethereum",
    hideSpam = true,
    signal
  } = params;
  const search = new URLSearchParams();
  search.set("query", query);
  search.set("chain", chain);
  const queryString = search.toString();
  const payload = await fetchJsonWithFailover<AlchemySearchResponse>(`/api/alchemy/collections?${queryString}`, `/collections?${queryString}`, {
    signal
  });
  return processSearchResponse(payload, hideSpam);
}
async function fetchContractOverviewFromApi(params: UseContractOverviewParams & {
  readonly signal?: AbortSignal;
}): Promise<ContractOverview | null> {
  const {
    address,
    chain = "ethereum",
    signal
  } = params;
  if (!address) {
    return null;
  }
  const checksum = normaliseAddress(address);
  if (!checksum) {
    return null;
  }
  const search = new URLSearchParams();
  search.set("address", address);
  search.set("chain", chain);
  const queryString = search.toString();
  const payload = await fetchJsonWithFailover<(AlchemyContractMetadataResponse & {
    _checksum?: string;
  }) | null>(`/api/alchemy/contract?${queryString}`, `/contract?${queryString}`, {
    signal
  });
  if (!payload) {
    return null;
  }
  const checksumFromResponse = (payload._checksum ?? checksum) as `0x${string}`;
  return processContractMetadataResponse(payload, checksumFromResponse);
}
async function fetchTokenMetadataFromApi(params: TokenMetadataParams): Promise<TokenMetadata[]> {
  const body = JSON.stringify({
    address: params.address,
    tokenIds: params.tokenIds,
    tokens: params.tokens,
    chain: params.chain ?? "ethereum"
  });
  const init: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body,
    signal: params.signal
  };
  const payload = await fetchJsonWithFailover<AlchemyTokenMetadataResponse>("/api/alchemy/token-metadata", "/token-metadata", init);
  return processTokenMetadataResponse(payload);
}
function gcExpired<T>(map: Map<string, CacheEntry<T>>, now = Date.now()): void {
  map.forEach((entry, key) => {
    if (entry.expires <= now) {
      map.delete(key);
    }
  });
}
function getSuggestionCacheKey(query: string, chain: SupportedChain, hideSpam: boolean): string {
  return `${chain}:${hideSpam ? "1" : "0"}:${query.toLowerCase()}`;
}
function getContractCacheKey(address: `0x${string}`, chain: SupportedChain): string {
  return `${chain}:${address.toLowerCase()}`;
}
function getTokenCacheKey(params: TokenMetadataParams): string {
  if (params.tokens) {
    const sortedTokens = [...params.tokens].sort((a, b) => {
      const contractCompare = a.contract.localeCompare(b.contract);
      if (contractCompare !== 0) {
        return contractCompare;
      }
      return a.tokenId.localeCompare(b.tokenId);
    });
    const key = sortedTokens.map(t => `${t.contract.toLowerCase()}:${t.tokenId}`).join("|");
    return `${params.chain ?? "ethereum"}:${key}`;
  }
  const address = params.address ?? "0x";
  const tokenIds = params.tokenIds ?? [];
  const ids = [...tokenIds].sort((a, b) => {
    if (a < b) {
      return -1;
    }
    if (a > b) {
      return 1;
    }
    return 0;
  });
  return `${params.chain ?? "ethereum"}:${address.toLowerCase()}:${ids.join("|")}`;
}
type UseCollectionSearchParams = {
  readonly query: string;
  readonly chain?: SupportedChain;
  readonly hideSpam?: boolean;
  readonly debounceMs?: number;
  readonly enabled?: boolean;
};
type UseContractOverviewParams = {
  readonly address?: `0x${string}`;
  readonly chain?: SupportedChain;
  readonly enabled?: boolean;
};
type UseTokenMetadataParams = {
  readonly address?: `0x${string}`;
  readonly tokenIds?: readonly string[];
  readonly tokens?: readonly {
    contract: string;
    tokenId: string;
  }[];
  readonly chain?: SupportedChain;
  readonly enabled?: boolean;
};
export function useCollectionSearch(t0) {
  const $ = _c(21);
  const {
    query,
    chain: t1,
    hideSpam: t2,
    debounceMs: t3,
    enabled: t4
  } = t0;
  const chain = t1 === undefined ? "ethereum" : t1;
  const hideSpam = t2 === undefined ? true : t2;
  const debounceMs = t3 === undefined ? 250 : t3;
  const enabled = t4 === undefined ? true : t4;
  const debouncedQuery = useDebouncedValue(query, debounceMs);
  const queryClient = useQueryClient();
  let t5;
  if ($[0] !== chain || $[1] !== debouncedQuery || $[2] !== hideSpam) {
    t5 = [QueryKey.NFT_COLLECTION_SEARCH, chain, debouncedQuery, hideSpam];
    $[0] = chain;
    $[1] = debouncedQuery;
    $[2] = hideSpam;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const t6 = enabled && Boolean(debouncedQuery);
  let t7;
  if ($[4] !== chain || $[5] !== debouncedQuery || $[6] !== hideSpam) {
    t7 = async t8 => {
      const {
        signal
      } = t8;
      const cacheKey = getSuggestionCacheKey(debouncedQuery, chain, hideSpam);
      const now = Date.now();
      gcExpired(suggestionCache, now);
      const cached = suggestionCache.get(cacheKey);
      if (cached && cached.expires > now) {
        return cached.data;
      }
      const data = await fetchCollectionsFromApi({
        query: debouncedQuery,
        chain,
        hideSpam,
        signal
      });
      suggestionCache.set(cacheKey, {
        data,
        expires: now + SUGGESTION_TTL
      });
      return data;
    };
    $[4] = chain;
    $[5] = debouncedQuery;
    $[6] = hideSpam;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t5 || $[9] !== t6 || $[10] !== t7) {
    t8 = {
      queryKey: t5,
      enabled: t6,
      staleTime: SUGGESTION_TTL,
      gcTime: SUGGESTION_TTL,
      queryFn: t7
    };
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const result = useQuery(t8);
  let t10;
  let t9;
  if ($[12] !== chain || $[13] !== debouncedQuery || $[14] !== hideSpam || $[15] !== queryClient) {
    t9 = () => {
      if (!debouncedQuery) {
        return;
      }
      const cacheKey_0 = getSuggestionCacheKey(debouncedQuery, chain, hideSpam);
      const now_0 = Date.now();
      gcExpired(suggestionCache, now_0);
      const cached_0 = suggestionCache.get(cacheKey_0);
      if (cached_0 && cached_0.expires > now_0) {
        queryClient.setQueryData([QueryKey.NFT_COLLECTION_SEARCH, chain, debouncedQuery, hideSpam], cached_0.data);
      }
    };
    t10 = [chain, debouncedQuery, hideSpam, queryClient];
    $[12] = chain;
    $[13] = debouncedQuery;
    $[14] = hideSpam;
    $[15] = queryClient;
    $[16] = t10;
    $[17] = t9;
  } else {
    t10 = $[16];
    t9 = $[17];
  }
  useEffect(t9, t10);
  let t11;
  if ($[18] !== debouncedQuery || $[19] !== result) {
    t11 = {
      ...result,
      debouncedQuery
    };
    $[18] = debouncedQuery;
    $[19] = result;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
}
export function useContractOverviewQuery(t0) {
  const $ = _c(12);
  const {
    address,
    chain: t1,
    enabled: t2
  } = t0;
  const chain = t1 === undefined ? "ethereum" : t1;
  const enabled = t2 === undefined ? true : t2;
  let t3;
  bb0: {
    if (!address) {
      t3 = undefined;
      break bb0;
    }
    let t4;
    if ($[0] !== address) {
      t4 = address.toLowerCase();
      $[0] = address;
      $[1] = t4;
    } else {
      t4 = $[1];
    }
    t3 = t4 as `0x${string}`;
  }
  const normalizedAddress = t3;
  let t4;
  if ($[2] !== chain || $[3] !== normalizedAddress) {
    t4 = [QueryKey.NFT_CONTRACT_OVERVIEW, chain, normalizedAddress];
    $[2] = chain;
    $[3] = normalizedAddress;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = enabled && Boolean(normalizedAddress);
  let t6;
  if ($[5] !== chain || $[6] !== normalizedAddress) {
    t6 = async t7 => {
      const {
        signal
      } = t7;
      if (!normalizedAddress) {
        return null;
      }
      const cacheKey = getContractCacheKey(normalizedAddress, chain);
      const now = Date.now();
      gcExpired(contractCache, now);
      const cached = contractCache.get(cacheKey);
      if (cached && cached.expires > now) {
        return cached.data;
      }
      const data = await fetchContractOverviewFromApi({
        address: normalizedAddress,
        chain,
        signal
      });
      contractCache.set(cacheKey, {
        data,
        expires: now + CONTRACT_TTL
      });
      return data;
    };
    $[5] = chain;
    $[6] = normalizedAddress;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t4 || $[9] !== t5 || $[10] !== t6) {
    t7 = {
      queryKey: t4,
      enabled: t5,
      staleTime: CONTRACT_TTL,
      gcTime: CONTRACT_TTL,
      queryFn: t6
    };
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return useQuery(t7);
}
export function useTokenMetadataQuery(t0) {
  const $ = _c(26);
  const {
    address,
    tokenIds,
    tokens,
    chain: t1,
    enabled: t2
  } = t0;
  const chain = t1 === undefined ? "ethereum" : t1;
  const enabled = t2 === undefined ? true : t2;
  let t3;
  bb0: {
    if (!tokenIds) {
      let t4;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = [];
        $[0] = t4;
      } else {
        t4 = $[0];
      }
      t3 = t4;
      break bb0;
    }
    let deduped;
    if ($[1] !== tokenIds) {
      const seen = new Set();
      deduped = [];
      for (const id of tokenIds) {
        if (!seen.has(id)) {
          seen.add(id);
          deduped.push(id);
        }
      }
      $[1] = tokenIds;
      $[2] = deduped;
    } else {
      deduped = $[2];
    }
    t3 = deduped;
  }
  const uniqueIds = t3;
  let t4;
  bb1: {
    if (tokens && tokens.length > 0) {
      let t5;
      if ($[3] !== chain || $[4] !== tokens) {
        t5 = {
          tokens,
          chain
        };
        $[3] = chain;
        $[4] = tokens;
        $[5] = t5;
      } else {
        t5 = $[5];
      }
      t4 = t5 satisfies TokenMetadataParams;
      break bb1;
    }
    if (!address || uniqueIds.length === 0) {
      t4 = undefined;
      break bb1;
    }
    let t5;
    if ($[6] !== address || $[7] !== chain || $[8] !== uniqueIds) {
      t5 = {
        address,
        tokenIds: uniqueIds,
        chain
      };
      $[6] = address;
      $[7] = chain;
      $[8] = uniqueIds;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    t4 = t5 satisfies TokenMetadataParams;
  }
  const params = t4;
  let t5;
  if ($[10] !== address) {
    t5 = address?.toLowerCase();
    $[10] = address;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== address) {
    t6 = address?.toLowerCase();
    $[12] = address;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== chain || $[15] !== t5 || $[16] !== t6 || $[17] !== tokens || $[18] !== uniqueIds) {
    t7 = [QueryKey.NFT_TOKEN_METADATA, chain, t5, QueryKey.NFT_TOKEN_METADATA, chain, t6, uniqueIds, tokens];
    $[14] = chain;
    $[15] = t5;
    $[16] = t6;
    $[17] = tokens;
    $[18] = uniqueIds;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const t8 = enabled && Boolean(params);
  let t9;
  if ($[20] !== params) {
    t9 = async t10 => {
      const {
        signal
      } = t10;
      if (!params) {
        return [] as TokenMetadata[];
      }
      const cacheKey = getTokenCacheKey(params);
      const now = Date.now();
      gcExpired(tokenCache, now);
      const cached = tokenCache.get(cacheKey);
      if (cached && cached.expires > now) {
        return cached.data;
      }
      const data = await fetchTokenMetadataFromApi({
        ...params,
        signal
      });
      tokenCache.set(cacheKey, {
        data,
        expires: now + TOKEN_TTL
      });
      return data;
    };
    $[20] = params;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t7 || $[23] !== t8 || $[24] !== t9) {
    t10 = {
      queryKey: t7,
      enabled: t8,
      placeholderData: keepPreviousData,
      staleTime: TOKEN_TTL,
      gcTime: TOKEN_TTL,
      queryFn: t9
    };
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return useQuery(t10);
}
export function primeContractCache(suggestion: Suggestion, chain: SupportedChain = "ethereum") {
  const cacheKey = getContractCacheKey(suggestion.address, chain);
  contractCache.set(cacheKey, {
    data: {
      ...suggestion,
      description: null,
      bannerImageUrl: null
    },
    expires: Date.now() + CONTRACT_TTL
  });
}
export async function fetchOwnerNfts(chainId: number, contract: string, owner: string, signal?: AbortSignal): Promise<OwnerNft[]> {
  const search = new URLSearchParams();
  search.set("chainId", String(chainId));
  search.set("contract", contract);
  search.set("owner", owner);
  const queryString = search.toString();
  const payload = await fetchJsonWithFailover<AlchemyGetNftsForOwnerResponse>(`/api/alchemy/owner-nfts?${queryString}`, `/owner-nfts?${queryString}`, {
    signal
  });
  return processOwnerNftsResponse(payload.ownedNfts ?? []);
}